import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useClickAway } from 'react-use';
import cn from 'classnames';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';
// Hooks
import { useGetGlobalSettings } from 'hooks/useGetGlobalSettings';
// Components
import { SearchForm } from './SearchForm';
import { Navigation } from './Navigation';
// Icons
import NavSearch from '../../../public/icons/nav-search.svg';
import Close from '../../../public/icons/close.svg';
import LandrLarge from '../../../public/icons/landr-large.svg';

import styles from './Menu.module.scss';

const getLocale = (locale?: string) => {
  switch (locale) {
    case 'en':
      return 'en';
    case 'fr':
      return 'fr';
    case 'de':
      return 'de';
    case 'ja':
      return 'ja';
    case 'es':
      return 'es';
    case 'pt':
    case 'pt-br':
      return 'pt';
    case 'it':
      return 'it';
    default:
      return 'en';
  }
};

export const Menu = () => {
  const { asPath, locale } = useRouter();

  const [isPageLoaded, setPageLoaded] = useState<boolean>(false);
  const [isMenuVisible, setMenuVisibility] = useState<boolean>(false);
  const menuContentRef = useRef<HTMLDivElement>(null);

  const settings = useGetGlobalSettings();

  const loginLabel = settings?.loginLabel || 'Login or Register';
  const loginLink =
    settings?.loginLink ||
    `https://app.landr.com/?locale=${getLocale(locale)}&isSignup=true`;
  const navigationTitle =
    settings?.navigationSlideHeadline ||
    'Learnings from the frontlines of music creation';
  const navigationSignOff =
    settings?.navigationSlideSignOff || 'Proudly brought to you by Landr';

  useClickAway(
    menuContentRef,
    () => {
      if (isMenuVisible) {
        setMenuVisibility(false);
        enableBodyScroll(document.body);
      }
    },
    ['click']
  );

  useEffect(() => {
    setMenuVisibility(false);
    clearAllBodyScrollLocks();
  }, [asPath]);

  // avoid initial menu animation
  useEffect(() => {
    setTimeout(() => {
      setPageLoaded(true);
    }, 500);
  }, []);

  const handleMenuToggle = (event: any) => {
    // otherwise "useClickAway" will be called
    event?.preventDefault();
    event?.stopPropagation();

    if (isMenuVisible) {
      setMenuVisibility(false);
      enableBodyScroll(document.body);
    } else {
      setMenuVisibility(true);
      disableBodyScroll(document.body);
    }
  };

  const Login = () => {
    return (
      <a
        href={loginLink}
        className={styles.cta}
        target="_blank"
        rel="noreferrer"
      >
        {loginLabel}
      </a>
    );
  };

  return (
    <div className={styles.root}>
      <Login />
      <button
        className={styles.menuToggleButton}
        onClick={handleMenuToggle}
        type="button"
        title="search"
      >
        <NavSearch className={styles.searchIcon} />
      </button>

      <div
        className={cn(styles.menu, {
          [styles.open]: isMenuVisible,
          [styles.loaded]: isPageLoaded,
        })}
      >
        <button
          className={styles.closeButton}
          onClick={handleMenuToggle}
          type="button"
          title="close menu"
        >
          <Close className={styles.closeIcon} />
        </button>

        <div className={styles.menuContent} ref={menuContentRef}>
          <p className={styles.title}>{navigationTitle}</p>
          <SearchForm handleMenuToggle={handleMenuToggle} />
          <Navigation />
          <div className={styles.copyrightWrapper}>
            <LandrLarge className={styles.landrLogo} />
            <p className={styles.copyrightText}>{navigationSignOff}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
